import type { MCPServerViewType } from "@app/lib/api/mcp";
import { AGENT_NAME_MAX_LENGTH } from "@app/types/assistant/agent";
import { MODEL_IDS } from "@app/types/assistant/models/models";
import { ReasoningEffortSchema } from "@app/types/assistant/models/reasoning";
import type { ModelConfigurationType } from "@app/types/assistant/models/types";
import type { SkillWithoutInstructionsAndToolsType } from "@app/types/assistant/skill_configuration";
import type { DataSourceViewType } from "@app/types/data_source_view";
import { assertNever } from "@app/types/shared/utils/assert_never";
import { z } from "zod";

export const AGENT_SUGGESTION_KINDS = [
  "instructions",
  "tools",
  "sub_agent",
  "skills",
  "model",
  "knowledge",
  "create",
  "delete",
  "name",
  "description",
  "scope",
  "editors",
  "tags",
  "structured_output",
] as const;

export type AgentSuggestionKind = (typeof AGENT_SUGGESTION_KINDS)[number];

export type SuggestionAction = "create" | "edit" | "delete";

export function getAgentSuggestionAction(
  kind: AgentSuggestionKind
): SuggestionAction {
  switch (kind) {
    case "create":
      return "create";
    case "delete":
      return "delete";
    case "description":
    case "editors":
    case "instructions":
    case "knowledge":
    case "model":
    case "name":
    case "scope":
    case "skills":
    case "structured_output":
    case "sub_agent":
    case "tags":
    case "tools":
      return "edit";
    default:
      return assertNever(kind);
  }
}

export const AGENT_SUGGESTION_STATES = [
  "pending",
  "approved",
  "rejected",
  "outdated",
] as const;

export type AgentSuggestionState = (typeof AGENT_SUGGESTION_STATES)[number];

// - `sidekick`: proposed while editing a specific agent.
// - `conversational`: proposed by an agent during a regular conversation.
export const AGENT_SUGGESTION_SOURCES = ["sidekick", "conversational"] as const;

export type AgentSuggestionSource = (typeof AGENT_SUGGESTION_SOURCES)[number];

export const INSTRUCTIONS_ROOT_TARGET_BLOCK_ID = "instructions-root";

const ToolsSuggestionSchema = z.object({
  action: z.enum(["add", "remove"]),
  toolId: z.string(),
});

const SubAgentSuggestionSchema = z.object({
  action: z.enum(["add", "remove"]),
  toolId: z.string(),
  childAgentId: z.string(),
});

const SkillsSuggestionSchema = z.object({
  action: z.enum(["add", "remove"]),
  skillId: z.string(),
});

const InstructionsSuggestionSchema = z.object({
  content: z
    .string()
    .describe("The full HTML content for this block, including the tag"),
  targetBlockId: z
    .string()
    .describe("The data-block-id of the block to modify"),
  type: z
    .enum(["replace"])
    .describe("The type of modification to perform on the target block"),
});

const ModelSuggestionSchema = z.object({
  modelId: z.enum(MODEL_IDS),
  reasoningEffort: ReasoningEffortSchema.optional(),
});

const CreateSuggestionSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1)
    .max(AGENT_NAME_MAX_LENGTH)
    .describe("Unique, human-readable agent name (no leading '@')."),
  description: z
    .string()
    .trim()
    .min(1)
    .describe(
      "Short description of what the agent does, shown to users browsing agents."
    ),
  instructions: z
    .string()
    .trim()
    .min(1)
    .describe("The agent's instructions, as HTML."),
  toolIds: z
    .array(z.string())
    .optional()
    .describe(
      "Ids of the tools (MCP server views) the agent is created with, each with its default configuration."
    ),
  skillIds: z
    .array(z.string())
    .optional()
    .describe("Ids of the skills the agent is created with."),
  subAgentIds: z
    .array(z.string())
    .optional()
    .describe("Ids of the agents the agent is created with as sub-agents."),
});

const DeleteSuggestionSchema = z.object({
  name: z.string().trim().min(1).describe("Name of the agent to delete."),
});

const NameSuggestionSchema = z.object({
  name: z.string().trim().min(1).max(AGENT_NAME_MAX_LENGTH),
});

const DescriptionSuggestionSchema = z.object({
  description: z.string().trim().min(1),
});

const ScopeSuggestionSchema = z.object({
  scope: z.enum(["hidden", "visible"]),
});

const EditorsSuggestionSchema = z
  .object({
    addUserIds: z
      .array(z.string())
      .describe("sIds of the workspace members to add as editors."),
    removeUserIds: z
      .array(z.string())
      .describe("sIds of the current editors to remove."),
  })
  .refine(
    (d) => d.addUserIds.length > 0 || d.removeUserIds.length > 0,
    "At least one of addUserIds or removeUserIds must be non-empty."
  );

// Tags are referenced by name, not id: a suggestion may add a tag that does not exist yet, which is
// created when the suggestion is applied.
const TagsSuggestionSchema = z
  .object({
    addTags: z.array(z.string()).describe("Names of the tags to add."),
    removeTags: z
      .array(z.string())
      .describe("Names of the agent's tags to remove."),
  })
  .refine(
    (d) => d.addTags.length > 0 || d.removeTags.length > 0,
    "At least one of addTags or removeTags must be non-empty."
  );

// The JSON schema the agent's answers must follow, as stored on its model configuration (a
// `ResponseFormat` serialized as JSON), or `null` to remove it.
const StructuredOutputSuggestionSchema = z.object({
  responseFormat: z.string().min(1).nullable(),
});

const KNOWLEDGE_SUGGESTION_METHODS = ["search", "query_tables"] as const;
const KnowledgeSuggestionSchema = z.object({
  action: z.enum(["add", "remove"]),
  method: z
    .enum(KNOWLEDGE_SUGGESTION_METHODS)
    .optional()
    .default("search")
    .describe(
      "'Search' for semantic search on unstructured data. 'Query table' to generate SQL-like queries against structured data."
    ),
  dataSourceViewId: z.string(),
  nodeIds: z.array(z.string()).optional(),
  description: z.string().optional(),
});

export type ToolsSuggestionType = z.infer<typeof ToolsSuggestionSchema>;
export type SubAgentSuggestionType = z.infer<typeof SubAgentSuggestionSchema>;
export type SkillsSuggestionType = z.infer<typeof SkillsSuggestionSchema>;
export type InstructionsSuggestionSchemaType = z.infer<
  typeof InstructionsSuggestionSchema
>;
export type ModelSuggestionType = z.infer<typeof ModelSuggestionSchema>;
export type KnowledgeSuggestionType = z.infer<typeof KnowledgeSuggestionSchema>;
export type CreateSuggestionType = z.infer<typeof CreateSuggestionSchema>;
export type DeleteSuggestionType = z.infer<typeof DeleteSuggestionSchema>;
export type DescriptionSuggestionType = z.infer<
  typeof DescriptionSuggestionSchema
>;
export type NameSuggestionType = z.infer<typeof NameSuggestionSchema>;
export type ScopeSuggestionType = z.infer<typeof ScopeSuggestionSchema>;
export type EditorsSuggestionType = z.infer<typeof EditorsSuggestionSchema>;
export type TagsSuggestionType = z.infer<typeof TagsSuggestionSchema>;
export type StructuredOutputSuggestionType = z.infer<
  typeof StructuredOutputSuggestionSchema
>;

export function isToolsSuggestion(data: unknown): data is ToolsSuggestionType {
  return ToolsSuggestionSchema.safeParse(data).success;
}

export function isSubAgentSuggestion(
  data: unknown
): data is SubAgentSuggestionType {
  return SubAgentSuggestionSchema.safeParse(data).success;
}

export function isSkillsSuggestion(
  data: unknown
): data is SkillsSuggestionType {
  return SkillsSuggestionSchema.safeParse(data).success;
}

export function isKnowledgeSuggestion(
  data: unknown
): data is KnowledgeSuggestionType {
  return KnowledgeSuggestionSchema.safeParse(data).success;
}

export type SuggestionPayload =
  | CreateSuggestionType
  | DeleteSuggestionType
  | DescriptionSuggestionType
  | EditorsSuggestionType
  | InstructionsSuggestionSchemaType
  | KnowledgeSuggestionType
  | ModelSuggestionType
  | NameSuggestionType
  | ScopeSuggestionType
  | SkillsSuggestionType
  | StructuredOutputSuggestionType
  | SubAgentSuggestionType
  | TagsSuggestionType
  | ToolsSuggestionType;

export const AgentSuggestionDataSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("tools"), suggestion: ToolsSuggestionSchema }),
  z.object({
    kind: z.literal("sub_agent"),
    suggestion: SubAgentSuggestionSchema,
  }),
  z.object({ kind: z.literal("skills"), suggestion: SkillsSuggestionSchema }),
  z.object({
    kind: z.literal("instructions"),
    suggestion: InstructionsSuggestionSchema,
  }),
  z.object({ kind: z.literal("model"), suggestion: ModelSuggestionSchema }),
  z.object({
    kind: z.literal("knowledge"),
    suggestion: KnowledgeSuggestionSchema,
  }),
  z.object({ kind: z.literal("create"), suggestion: CreateSuggestionSchema }),
  z.object({ kind: z.literal("delete"), suggestion: DeleteSuggestionSchema }),
  z.object({ kind: z.literal("name"), suggestion: NameSuggestionSchema }),
  z.object({
    kind: z.literal("description"),
    suggestion: DescriptionSuggestionSchema,
  }),
  z.object({ kind: z.literal("scope"), suggestion: ScopeSuggestionSchema }),
  z.object({
    kind: z.literal("editors"),
    suggestion: EditorsSuggestionSchema,
  }),
  z.object({ kind: z.literal("tags"), suggestion: TagsSuggestionSchema }),
  z.object({
    kind: z.literal("structured_output"),
    suggestion: StructuredOutputSuggestionSchema,
  }),
]);

export type AgentSuggestionData = z.infer<typeof AgentSuggestionDataSchema>;

export function parseAgentSuggestionData(data: unknown): AgentSuggestionData {
  return AgentSuggestionDataSchema.parse(data);
}

const BaseAgentSuggestionSchema = z.object({
  id: z.number(),
  sId: z.string(),
  createdAt: z.number(),
  updatedAt: z.number(),
  // TODO(conversational-building): drop `agentConfigurationId`, a model id, in favor of `agentId`.
  agentConfigurationId: z.number(),
  // The sId of the agent the suggestion targets.
  agentId: z.string(),
  analysis: z.string().nullable(),
  state: z.enum(AGENT_SUGGESTION_STATES),
  source: z.enum(AGENT_SUGGESTION_SOURCES),
  conversationId: z.string().nullable(),
  batchId: z.string().nullable(),
});

export const AgentSuggestionSchema = BaseAgentSuggestionSchema.and(
  AgentSuggestionDataSchema
);

export type AgentSuggestionType = z.infer<typeof AgentSuggestionSchema>;

export type AgentInstructionsSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "instructions" }
>;

export type AgentToolsSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "tools" }
>;

export type AgentSubAgentSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "sub_agent" }
>;

export type AgentSkillsSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "skills" }
>;

export type AgentModelSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "model" }
>;

export type AgentKnowledgeSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "knowledge" }
>;

export type AgentCreateSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "create" }
>;

export function isCreateAgentSuggestion(
  suggestion: AgentSuggestionType
): suggestion is AgentCreateSuggestionType {
  return suggestion.kind === "create";
}

export type AgentDeleteSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "delete" }
>;

export type AgentDescriptionSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "description" }
>;

export type AgentNameSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "name" }
>;

export type AgentScopeSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "scope" }
>;

export type AgentEditorsSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "editors" }
>;

export type AgentTagsSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "tags" }
>;

export type AgentStructuredOutputSuggestionType = Extract<
  AgentSuggestionType,
  { kind: "structured_output" }
>;

export interface ToolSuggestionRelations {
  tool: MCPServerViewType;
}

export interface SubAgentSuggestionRelations {
  tool: MCPServerViewType;
}

export interface SkillSuggestionRelations {
  skill: SkillWithoutInstructionsAndToolsType;
}

export interface ModelSuggestionRelations {
  model: ModelConfigurationType;
}

export interface KnowledgeSuggestionRelations {
  dataSourceView: DataSourceViewType;
  serverView: MCPServerViewType;
}

export type AgentToolsSuggestionWithRelationsType = AgentToolsSuggestionType & {
  relations: ToolSuggestionRelations;
};

export type AgentSubAgentSuggestionWithRelationsType =
  AgentSubAgentSuggestionType & { relations: SubAgentSuggestionRelations };

export type AgentSkillsSuggestionWithRelationsType =
  AgentSkillsSuggestionType & { relations: SkillSuggestionRelations };

export type AgentModelSuggestionWithRelationsType = AgentModelSuggestionType & {
  relations: ModelSuggestionRelations;
};

export type AgentKnowledgeSuggestionWithRelationsType =
  AgentKnowledgeSuggestionType & { relations: KnowledgeSuggestionRelations };

export type AgentSuggestionWithRelationsType =
  | (AgentCreateSuggestionType & { relations: null })
  | (AgentDeleteSuggestionType & { relations: null })
  | (AgentDescriptionSuggestionType & { relations: null })
  | (AgentEditorsSuggestionType & { relations: null })
  | (AgentInstructionsSuggestionType & { relations: null })
  | AgentKnowledgeSuggestionWithRelationsType
  | AgentModelSuggestionWithRelationsType
  | (AgentNameSuggestionType & { relations: null })
  | (AgentScopeSuggestionType & { relations: null })
  | AgentSkillsSuggestionWithRelationsType
  | (AgentStructuredOutputSuggestionType & { relations: null })
  | AgentSubAgentSuggestionWithRelationsType
  | (AgentTagsSuggestionType & { relations: null })
  | AgentToolsSuggestionWithRelationsType;
